<template>
  <div class="favorites-page">
    <Preloader :backgroundColor="preloaderColor"></Preloader>

    <!-- Header -->
    <HeaderNews
      subtitle="Noticias y contenido sobre Inteligencia Artificial"
      :showSearch="true"
      :searchQuery.sync="query"
      :showMobileMenu="true"
      @toggle-mobile-menu="toggleMobileMenu"
    />

    <!-- Main Layout -->
    <div class="favorites-layout">
      <!-- Sidebar -->
      <Sidebar :class="{'sidebar--mobile-visible': showMobileSidebar}" @navigate="showMobileSidebar = false" />

      <!-- Mobile Overlay -->
      <div
        v-if="showMobileSidebar"
        class="mobile-overlay"
        @click="toggleMobileMenu"
      ></div>

      <!-- Main Content -->
      <div class="favorites-body">
        <main class="favorites-main">
          <div class="favorites-container">
        <!-- Header con título -->
        <div v-if="filteredFavorites.length > 0" class="favorites-header">
          <h2 class="favorites-header__title">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
            </svg>
            Favoritos ({{ filteredFavorites.length }})
          </h2>
        </div>

        <!-- Empty state -->
        <div v-if="filteredFavorites.length === 0 && !loading" class="empty-state">
          <div class="empty-state__icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
            </svg>
          </div>
          <h2 class="empty-state__title">No tienes favoritos</h2>
          <p class="empty-state__message">
            Los artículos que marques como favoritos aparecerán aquí
          </p>
          <router-link to="/news" class="empty-state__button">
            Ver todos los artículos
          </router-link>
        </div>

        <!-- Feed List -->
        <FeedList v-else :items="filteredFavorites" :loading="loading" />
          </div>
        </main>

        <!-- Footer -->
        <Footer />
      </div>
    </div>
  </div>
</template>

<script>
import HeaderNews from '../components/HeaderNews';
import Sidebar from '../components/Sidebar';
import FeedList from '../components/FeedList';
import Footer from '../components/Footer';
import bookmarksService from '../../../services/bookmarks.js';
import Preloader from '../../../components/Preloader';

export default {
  name: 'Favorites',
  components: {
    HeaderNews,
    Sidebar,
    FeedList,
    Footer,
    Preloader
  },
  data() {
    return {
      favorites: [],
      query: '',
      loading: false,
      showMobileSidebar: false,
      preloaderColor: 'rgba(14, 18, 23, 0.8)', // Color de fondo del preloader
    };
  },
  computed: {
    filteredFavorites() {
      if (!this.query.trim()) {
        return this.favorites;
      }

      const q = this.query.trim().toLowerCase();
      return this.favorites.filter(item =>
        (item.title && item.title.toLowerCase().includes(q)) ||
        (item.description && item.description.toLowerCase().includes(q))
      );
    }
  },
  methods: {
    loadFavorites() {
      this.favorites = bookmarksService.getFavorites();
    },
    onBookmarkChange() {
      this.loadFavorites();
    },
    toggleMobileMenu() {
      this.showMobileSidebar = !this.showMobileSidebar;
    }
  },
  mounted() {
    this.loadFavorites();
    window.addEventListener('bookmarks-updated', this.onBookmarkChange);
  },
  beforeUnmount() {
    window.removeEventListener('bookmarks-updated', this.onBookmarkChange);
  }
};
</script>

<style scoped>
.favorites-page {
  min-height: 100vh;
  background: #0e1217;
  color: #e1e7ef;
  display: flex;
  flex-direction: column;
}

/* Main Layout */
.favorites-layout {
  display: flex;
  flex: 1;
  position: relative;
}

.favorites-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  margin-left: 240px; /* Ancho del sidebar */
  min-height: calc(100vh - 57px);
  transition: margin-left 0.3s ease;
}

.favorites-main {
  flex: 1;
  overflow-y: auto;
  padding: 24px 20px 80px; /* Padding bottom para el footer */
}

/* Scrollbar para favorites-main */
.favorites-main::-webkit-scrollbar {
  width: 6px;
}
.favorites-main::-webkit-scrollbar-track {
  background: #1c2127;
  border-radius: 3px;
}
.favorites-main::-webkit-scrollbar-thumb {
  background: #2d333b;
  border-radius: 3px;
}
.favorites-main::-webkit-scrollbar-thumb:hover {
  background: #4a9eff;
}

.favorites-container {
  max-width: 1400px;
  margin: 0 auto;
}

/* Mobile Overlay */
.mobile-overlay {
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 400;
  backdrop-filter: blur(4px);
}

.sidebar--mobile-visible {
  left: 0 !important;
}

/* Header de favoritos */
.favorites-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #2d333b;
}

.favorites-header__title {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 20px;
  font-weight: 700;
  color: #e1e7ef;
  margin: 0;
}

.favorites-header__title svg {
  color: #4a9eff;
}

/* Empty state */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
}

.empty-state__icon {
  width: 80px;
  height: 80px;
  background: rgba(74, 158, 255, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  color: #4a9eff;
}

.empty-state__title {
  font-size: 24px;
  font-weight: 700;
  color: #e1e7ef;
  margin: 0 0 12px 0;
}

.empty-state__message {
  font-size: 16px;
  color: #7d8694;
  margin: 0 0 32px 0;
  max-width: 400px;
  line-height: 1.5;
}

.empty-state__button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: linear-gradient(135deg, #4a9eff, #7b68ee);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(74, 158, 255, 0.3);
  text-decoration: none;
}

.empty-state__button:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(74, 158, 255, 0.4);
}

@media (max-width: 768px) {
  .mobile-overlay {
    display: block;
  }

  .favorites-body {
    margin-left: 0; /* Sin margen en mobile */
  }

  .favorites-main {
    padding: 16px 12px 60px; /* Ajustar padding bottom */
  }

  .favorites-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
}

@media (max-width: 480px) {
  .favorites-main {
    padding: 12px 8px;
  }

  .favorites-header__title {
    font-size: 18px;
  }
}
</style>
